<script lang="ts">
    import { onMount } from 'svelte';
    import { get } from 'svelte/store';
    import { _ } from 'svelte-i18n';

    import { api } from '../../../stores/api';
    import { AddressSchemaResponse, type AddressResponseType, JobModifyTimeWindowRuleApi } from './JobModifyTimeWindowRuleApi';
    import type {
        JobModifyTimeWindowRuleRequestType,
        JobModifyTimeWindowRuleResponseType,
        CountType,
    } from './JobModifyTimeWindowRuleApi';
    import Spinner from '../../shared/Spinner/Spinner.svelte';
    import MessageDanger from '../../shared/Messages/MessageDanger.svelte';
    import MessageNotFound from '../../shared/Messages/MessageNotFound.svelte';
    import MessageFiltered from '../../shared/MessageFiltered.svelte';
    import Separator from '../../shared/Separator.svelte';
    import { blur } from 'svelte/transition';
    import Checkbox from '../../shared/Checkbox/Checkbox.svelte';
    import ButtonSmallBlueRounded from '../../shared/ButtonSmallBlueRounded.svelte';
    import IconRemove from '../../shared/Icons/IconRemove.svelte';
    import IconStylus from '../../shared/Icons/IconStylus.svelte';
    import IconCheck from '../../shared/Icons/IconCheck.svelte';
    import Switch from '../../shared/Switch.svelte';
    import SelectHour from '../../shared/SelectHour.svelte';
    import IconCancel from '../../shared/Icons/IconCancel.svelte';

    export let addMode: boolean = false;
    export let reloadTrigger: number = 0;
    export let searchQuery: string = '';

    let rules: JobModifyTimeWindowRuleResponseType[] = [];
    let error: string = '';
    let loading: boolean = true;
    let countTotal: CountType = { count: 0 };
    let editingRuleId: number | null = null;

    // Domyślne wartości dla nowej reguły
    const defaultRule: JobModifyTimeWindowRuleRequestType = {
        enabled: true,
        address_id: null,
        name_part: '',
        start_of_time_window: '08:00',
        end_of_time_window: '16:00',
    };

    let newRule: JobModifyTimeWindowRuleRequestType = { ...defaultRule };
    let editedRule: JobModifyTimeWindowRuleRequestType = { ...defaultRule };

    function reloadRules() {
        error = '';
        loading = true;
        Promise.all([JobModifyTimeWindowRuleApi.getAll(get(api)), JobModifyTimeWindowRuleApi.getCount(get(api))])
            .then(([rulesResponse, countResponse]) => {
                rules = rulesResponse;
                countTotal = countResponse;
                addMode = false;
                editingRuleId = null;
            })
            .catch(err => {
                error = err;
            })
            .finally(() => {
                loading = false;
            });
    }


    function handleUpdate(ruleId: number) {
        // Walidacja
        if (!editedRule.start_of_time_window && !editedRule.end_of_time_window) {
            alert($_('Proszę wypełnić okna czasowe'));
            return;
        }

        if (!editedRule.address_id && (!editedRule.name_part || editedRule.name_part.trim() === '')) {
            alert($_('Proszę podać ID adresu lub fragment nazwy'));
            return;
        }

        loading = true;
        JobModifyTimeWindowRuleApi.updateOne(get(api), ruleId, editedRule)
            .then(() => {
                reloadRules();
            })
            .catch(err => {
                error = err;
                loading = false;
            });
    }

    function handleDelete(ruleId: number) {
        if (!confirm($_('Czy na pewno chcesz usunąć tę regułę?'))) {
            return;
        }

        loading = true;
        JobModifyTimeWindowRuleApi.removeOne(get(api), ruleId)
            .then(() => {
                reloadRules();
            })
            .catch(err => {
                error = err;
                loading = false;
            });
    }

    function startEdit(rule: JobModifyTimeWindowRuleResponseType) {
        editingRuleId = rule.id;
        editedRule = {
            enabled: rule.enabled,
            address_id: rule.address_id,
            name_part: rule.name_part,
            start_of_time_window: rule.start_of_time_window,
            end_of_time_window: rule.end_of_time_window,
        };
    }

    function cancelEdit() {
        editingRuleId = null;
        editedRule = { ...defaultRule };
    }

    function displayAddress(address: AddressResponseType) {
        if (address) {
            return `${address.pcode} ${address.city}, ${address.street} ${address.house}`;
        } else {
            return '---';
        }
    }

    onMount(() => {
        reloadRules();
    });

    // Reaguj na zmiany reloadTrigger - odświeżaj listę gdy się zmieni
    $: if (reloadTrigger > 0) {
        console.log('JobModifyTimeWindowRule, reloadTrigger', reloadTrigger);
        reloadRules();
    }

    $: filteredRules = searchQuery.trim() === ''
        ? rules
        : rules.filter(rule => {
            const q = searchQuery.toLowerCase();
            const addr = rule.address;
            return (
                (addr?.city ?? '').toLowerCase().includes(q) ||
                (addr?.street ?? '').toLowerCase().includes(q) ||
                (addr?.pcode ?? '').toLowerCase().includes(q) ||
                (addr?.house ?? '').toLowerCase().includes(q) ||
                (rule.name_part ?? '').toLowerCase().includes(q) ||
                (rule.start_of_time_window ?? '').includes(q) ||
                (rule.end_of_time_window ?? '').includes(q)
            );
        });
</script>

{#if loading}
    <Spinner />
{:else if error}
    <MessageDanger {error} />
{:else}
    <MessageFiltered filteredCount={filteredRules.length} totalCount={countTotal.count} />

    {#if countTotal.count === 0}
        <MessageNotFound text={$_('no_rules')} />
    {:else}
        <Separator />
        <div class="rules-list">
            <div class="rules-header">
                <div class="col-enabled">{$_('active')}</div>
                <div class="col-address">{$_('Address')}</div>
<!--                <div class="col-name">Nazwa zlecenia</div>-->
                <div class="col-time">{$_('Time_window')}</div>
                <div class="col-actions">{$_('actions')}</div>
            </div>
            {#each filteredRules as rule (rule.id)}
                <div class="rule-row" transition:blur>
                    {#if editingRuleId === rule.id}
                        <!-- Tryb edycji -->
                        <div class="col-enabled">
                            <Switch bind:checked={rule.enabled} label=""
                                    on:change={e => {
                                        editedRule.enabled = e.detail.checked;
                                        JobModifyTimeWindowRuleApi.setEnabled(get(api), rule.id, e.detail.checked)
                                    }}
                            />
                        </div>
                        <div class="col-address">{rule.address ? displayAddress(rule.address) : '---'}</div>
<!--                        <div class="col-name">-->
<!--                            {rule.name_part ?? '-'}-->
<!--                        </div>-->
                        <div class="col-time">
                            <SelectHour
                                canBeEmpty={true}
                                withBorder={false} hour={editedRule.start_of_time_window || ''} onChange={h => {
                                editedRule.start_of_time_window = h || null;
                                return true;
                            }}/>
                            -
                            <SelectHour
                                canBeEmpty={true}
                                withBorder={false} hour={editedRule.end_of_time_window || ''} onChange={h => {
                                editedRule.end_of_time_window = h || null;
                                return true;
                            }}/>
                        </div>
                        <div class="col-actions">
                            <ButtonSmallBlueRounded text={$_('Save')} on:click={() => handleUpdate(rule.id)} />
                            <IconCancel scale={0.65} on:click={cancelEdit} hover={$_('Cancel')} />
                        </div>
                    {:else}
                        <!-- Tryb wyświetlania -->
                        <div class="col-enabled">
                            <!--{rule.enabled ? '✓' : '✗'}-->
                            <Switch bind:checked={rule.enabled} label=""
                                    on:change={e => {
                                        JobModifyTimeWindowRuleApi.setEnabled(get(api), rule.id, e.detail.checked)
                                    }}
                            />
                        </div>
<!--                        <div class="col-id">{rule.id}</div>-->
                        <div class="col-address">{rule.address ? displayAddress(rule.address) : '---'}</div>
<!--                        <div class="col-name">{rule.name_part ?? '-'}</div>-->
                        <div class="col-time">
                            {rule.start_of_time_window ?? '-'} - {rule.end_of_time_window ?? '-'}
                        </div>
                        <div class="col-actions">
                            <button
                                class="action-btn"
                                on:click={() => startEdit(rule)}
                                title={$_('Edytuj')}
                            >
                                <IconStylus scale={0.65}/>
                            </button>
                            <button
                                class="action-btn danger"
                                on:click={() => handleDelete(rule.id)}
                                title={$_('Usuń')}
                            >
                                <IconRemove  scale={0.65}/>
                            </button>
                        </div>
                    {/if}
                </div>
            {/each}
        </div>
    {/if}
{/if}

<style>
    .rule-form {
        background: #f9f9f9;
        padding: 20px;
        border-radius: 8px;
        margin-bottom: 20px;
    }

    .form-row {
        display: flex;
        gap: 15px;
        margin-bottom: 15px;
        align-items: center;
    }

    .form-row label {
        display: flex;
        flex-direction: column;
        gap: 5px;
        flex: 1;
    }

    .checkbox-label {
        flex-direction: row !important;
        align-items: center;
        gap: 8px !important;
    }

    .input-field {
        padding: 8px;
        border: 1px solid #ccc;
        border-radius: 4px;
        font-size: 14px;
    }

    .input-field-small {
        padding: 4px 8px;
        border: 1px solid #ccc;
        border-radius: 4px;
        font-size: 13px;
        width: 100%;
    }

    .form-actions {
        display: flex;
        gap: 10px;
        margin-top: 15px;
    }

    .rules-list {
        display: flex;
        flex-direction: column;
    }

    .rules-header,
    .rule-row {
        display: grid;
        grid-template-columns: 50px 1fr 0.5fr 120px;
        gap: 10px;
        padding: 3px;
        align-items: center;
        border-bottom: 1px solid #e0e0e0;
    }

    .rules-header {
        font-weight: bold;
        background: #f5f5f5;
        position: sticky;
        top: 0;
        z-index: 10;
    }

    .rule-row:hover {
        background: #fafafa;
    }

    .col-time {
        display: flex;
        gap: 5px;
        align-items: center;
    }

    .col-actions {
        display: flex;
        gap: 1px;
        align-items: center;
    }

    .action-btn {
        background: none;
        border: none;
        cursor: pointer;
        padding: 4px;
        opacity: 0.6;
        transition: opacity 0.2s;
    }

    .action-btn:hover {
        opacity: 1;
    }

    .action-btn.danger:hover {
        color: #d32f2f;
    }
</style>